import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AvaliacaoDesign, jaAvaliouDesign } from "@/components/AvaliacaoDesign";
import { Botao } from "@/components/sistema";
import { useAuth } from "@/contexts/AuthContext";
import { EVENTO_RECEITA_ABERTA, jaUsouOApp } from "@/lib/receitasAbertas";
import s from "./AvisoDesignNovo.module.css";

const LS_VISTO = "aviso-design-2026-09-visto";

/**
 * O aviso de que o app mudou, uma vez por pessoa.
 *
 * Substitui o `NewLayoutModal`, que perguntava qual dos dois desenhos a pessoa
 * queria. Não há mais dois: há um, e o que se pergunta agora é se ela gostou.
 *
 * Dois passos e não um: primeiro o aviso com "ver o app", depois a avaliação.
 * Pedir opinião antes de a pessoa ter usado a tela produz um voto sobre um
 * aviso, não sobre o app. Quem só quer entrar toca em "ver" e a pergunta volta
 * em Ajustes, onde ela decide a hora.
 *
 * Aparece quando: autenticado, com TRÊS receitas já abertas, ainda não visto
 * neste aparelho e ainda não avaliado. Sem Supabase no caminho — o controle é
 * local, porque um aviso que espera a rede é um aviso que aparece depois da
 * tela que ele anuncia.
 *
 * A terceira receita é a condição nova. Antes bastava estar autenticado, e o
 * aviso cobria a primeira tela da sessão: a pessoa entrava e era interrompida
 * por uma pergunta sobre um app que ela ainda não tinha visto. A resposta
 * disso veio escrita — "You are asking me to rate website you haven't let me
 * see YET" — e virou nota baixa fabricada pelo próprio app.
 */
export default function AvisoDesignNovo() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const [fechado, setFechado] = useState(() => {
    try {
      return localStorage.getItem(LS_VISTO) === "1" || jaAvaliouDesign();
    } catch {
      return false;
    }
  });
  const [avaliando, setAvaliando] = useState(false);
  const [usou, setUsou] = useState(jaUsouOApp);

  // O contador sobe enquanto este componente já está montado, então ele
  // escuta em vez de ler uma vez só.
  useEffect(() => {
    if (usou) return;
    const aoAbrir = () => setUsou(jaUsouOApp());
    window.addEventListener(EVENTO_RECEITA_ABERTA, aoAbrir);
    return () => window.removeEventListener(EVENTO_RECEITA_ABERTA, aoAbrir);
  }, [usou]);

  if (!isAuthenticated || fechado || !usou) return null;

  const fechar = () => {
    try { localStorage.setItem(LS_VISTO, "1"); } catch { /* sem armazenamento */ }
    setFechado(true);
  };

  return (
    <div className={`${s.overlay} bg-black/60`} role="dialog" aria-modal="true" aria-labelledby="aviso-design-titulo">
      <div className={s.card}>
        {avaliando ? (
          <>
            <div style={{ marginLeft: "calc(var(--gutter) * -1)", marginRight: "calc(var(--gutter) * -1)" }}>
              <AvaliacaoDesign onPronto={fechar} />
            </div>
            <button
              type="button"
              onClick={fechar}
              className="mt-4 w-full font-semibold underline"
              style={{ minHeight: "var(--alvo-min)", fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)" }}
            >
              {t("design_rating.later", "Ahora no")}
            </button>
          </>
        ) : (
          <>
            <h2
              id="aviso-design-titulo"
              className="font-bold leading-tight"
              style={{ fontSize: "var(--t-item)" }}
            >
              {t("design_notice.title", "El app cambió de cara")}
            </h2>
            <p
              className="mt-3 leading-relaxed"
              style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)" }}
            >
              {t(
                "design_notice.body",
                "Letras más grandes, botones más grandes y todo lo que compraste en la primera pantalla. Tus recetas, tus listas y tus guardados siguen donde estaban.",
              )}
            </p>
            <div className="mt-6 grid gap-3">
              <Botao rotulo={t("design_notice.see", "Ver el app")} onClick={fechar} />
              <Botao
                rotulo={t("design_notice.rate", "Decir qué me pareció")}
                variante="secundaria"
                onClick={() => setAvaliando(true)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
